import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@@/store';
import { setEmbedToken } from '@@/features/auth';
import {
  EMBED_HANDSHAKE_TIMEOUT_MS,
  EMBED_MESSAGES,
  MAX_AUTH_EXPIRED_RETRIES,
  MIN_EMBED_HEIGHT,
  getAllowedParentOrigins,
  isAllowedParentOrigin,
  isFramed,
  unembedPath,
} from '@@/utils/embed';

/**
 * Layout des routes /embed/* : pas de sidebar, pas de redirection vers le
 * formulaire de connexion, et une session fournie par l'application parente.
 *
 * Handshake : l'iframe annonce `ready`, le parent répond avec un token court.
 * Le token ne transite jamais par l'URL (sinon il finirait dans les access logs
 * CloudFront, l'historique et le Referer).
 */
const EmbedRoute: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const parentOriginRef = useRef<string>('');
  const authExpiredCountRef = useRef(0);
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const [hasReceivedToken, setHasReceivedToken] = useState(false);
  const [handshakeFailed, setHandshakeFailed] = useState(false);
  const [viewport, setViewport] = useState<{
    top: number;
    height: number;
  } | null>(null);

  const framed = isFramed();

  const postToParent = useCallback(
    (message: Record<string, unknown>) => {
      if (!framed) {
        return;
      }

      // Aucun message sortant ne contient de secret (ready / height /
      // auth-expired), donc '*' est sûr tant que le parent ne s'est pas
      // annoncé — et c'est ce qui permet de lui remonter une hauteur dès
      // l'écran de chargement, avant tout token.
      window.parent.postMessage(message, parentOriginRef.current || '*');
    },
    [framed],
  );

  useEffect(() => {
    if (!framed) {
      return undefined;
    }

    const onMessage = (event: MessageEvent) => {
      const data = event.data as {
        type?: string;
        accessToken?: string;
        expiresIn?: number;
        top?: number;
        height?: number;
      };
      const type = data?.type;

      if (type !== EMBED_MESSAGES.TOKEN && type !== EMBED_MESSAGES.VIEWPORT) {
        return;
      }

      // Sans ce test, n'importe quelle fenêtre partageant l'origine du parent
      // (popup, frame imbriquée, l'app elle-même en dev) pourrait injecter le
      // bearer token que l'on attache ensuite à toutes les requêtes.
      if (event.source !== window.parent) {
        return;
      }

      if (!isAllowedParentOrigin(event.origin)) {
        const allowed = getAllowedParentOrigins().join(', ') || '(vide)';
        console.warn(
          `[embed] message "${type}" ignoré : origine "${event.origin}" absente de VITE_EMBED_PARENT_ORIGINS — origines autorisées : ${allowed}`,
        );
        return;
      }

      parentOriginRef.current = event.origin;

      if (type === EMBED_MESSAGES.VIEWPORT) {
        if (
          typeof data.top !== 'number' ||
          typeof data.height !== 'number' ||
          data.height <= 0
        ) {
          return;
        }

        setViewport({ top: Math.max(data.top, 0), height: data.height });
        return;
      }

      if (!data.accessToken) {
        return;
      }

      authExpiredCountRef.current = 0;
      setHandshakeFailed(false);
      setHasReceivedToken(true);
      dispatch(
        setEmbedToken({
          accessToken: data.accessToken,
          expiresIn: data.expiresIn || 0,
        }),
      );
    };

    window.addEventListener('message', onMessage);

    // `ready` ne contient aucun secret, d'où le '*' : on ne connaît pas encore
    // l'origine du parent, c'est justement sa réponse qui nous l'apprend.
    window.parent.postMessage({ type: EMBED_MESSAGES.READY }, '*');

    return () => window.removeEventListener('message', onMessage);
  }, [dispatch, framed]);

  // Une origine parente qui ne correspond pas à l'octet près à l'allow-list
  // laisserait l'iframe sur son écran de chargement pour toujours : au bout du
  // délai on bascule sur un message explicite (le console.warn ci-dessus donne
  // l'origine fautive).
  useEffect(() => {
    if (!framed || hasReceivedToken) {
      return undefined;
    }

    const timer = window.setTimeout(
      () => setHandshakeFailed(true),
      EMBED_HANDSHAKE_TIMEOUT_MS,
    );

    return () => window.clearTimeout(timer);
  }, [framed, hasReceivedToken]);

  // Le parent dimensionne l'iframe sur cette hauteur : pas de double scrollbar.
  useEffect(() => {
    const node = containerRef.current;

    if (!node) {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      postToParent({
        type: EMBED_MESSAGES.HEIGHT,
        height: Math.max(node.scrollHeight, MIN_EMBED_HEIGHT),
      });
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, [postToParent]);

  // Les overlays `position: fixed` (react-modal, react-hot-toast) s'ancrent sur
  // la hauteur totale de l'iframe. Ces variables leur donnent la zone
  // réellement visible chez le parent (cf. index.css).
  useEffect(() => {
    const root = document.documentElement;

    if (!viewport) {
      root.style.removeProperty('--embed-viewport-top');
      root.style.removeProperty('--embed-viewport-height');
      return;
    }

    root.style.setProperty('--embed-viewport-top', `${viewport.top}px`);
    root.style.setProperty('--embed-viewport-height', `${viewport.height}px`);
  }, [viewport]);

  // Un 401 fait tomber le token via baseQueryWithReauth / errorHandler. On
  // prévient le parent, qui en redemande un plutôt que d'afficher un login.
  useEffect(() => {
    if (!hasReceivedToken || accessToken) {
      return undefined;
    }

    // Un parent qui renvoie le token déjà expiré ferait boucler le couple
    // 401 / auth-expired à pleine vitesse : on espace, puis on abandonne.
    if (authExpiredCountRef.current >= MAX_AUTH_EXPIRED_RETRIES) {
      setHandshakeFailed(true);
      return undefined;
    }

    const attempt = authExpiredCountRef.current;
    const timer = window.setTimeout(
      () => {
        authExpiredCountRef.current += 1;
        postToParent({ type: EMBED_MESSAGES.AUTH_EXPIRED });
      },
      attempt === 0 ? 0 : 1000 * 2 ** attempt,
    );

    return () => window.clearTimeout(timer);
  }, [hasReceivedToken, accessToken, postToParent]);

  // `/embed/...` ouvert au premier plan : la version normale sait rejouer la
  // session persistée et, à défaut, afficher le formulaire de connexion.
  if (!framed) {
    return (
      <Navigate
        to={`${unembedPath(location.pathname)}${location.search}`}
        replace
      />
    );
  }

  return (
    <div ref={containerRef} className="w-full bg-neutral-50 relative">
      {hasReceivedToken ? (
        // Une fois la première session reçue, l'arbre reste monté : le
        // démonter à chaque 401 viderait les formulaires en cours de saisie.
        <Outlet />
      ) : (
        <div className="p-8 text-center">
          <p className="text-body text-neutral-500">
            {handshakeFailed
              ? "Impossible de charger les incidents : l'application hôte n'a pas fourni de session."
              : 'Chargement des incidents…'}
          </p>
        </div>
      )}

      {hasReceivedToken && !accessToken && (
        <div
          className="absolute left-0 right-0 z-10 bg-neutral-50/90 p-8 text-center"
          style={{ top: 'var(--embed-viewport-top, 0px)' }}
        >
          <p className="text-body text-neutral-500">
            {handshakeFailed
              ? "Session expirée. Rechargez la page depuis l'application hôte."
              : 'Session expirée, reconnexion en cours…'}
          </p>
        </div>
      )}
    </div>
  );
};

export default EmbedRoute;
